import { useCallback, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ShortTextIcon from '@mui/icons-material/ShortText';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface ParagraphContextMenuProps {
  children: ReactNode;
  onImageGenerate: () => void;
  onAudioGenerate: () => void;
  onTextGenerate: () => void;
  onExpand: () => void;
  onRewrite: () => void;
  onSummarize: () => void;
  onFindSimilar: () => void;
}

export function ParagraphContextMenu({
  children,
  onImageGenerate,
  onAudioGenerate,
  onTextGenerate,
  onExpand,
  onRewrite,
  onSummarize,
  onFindSimilar,
}: ParagraphContextMenuProps) {
  const [anchorPosition, setAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleContextMenu = useCallback((e: MouseEvent) => {
    e.preventDefault();
    setAnchorPosition({ top: e.clientY, left: e.clientX });
  }, []);

  const handleClose = useCallback(() => {
    setAnchorPosition(null);
  }, []);

  const invoke = useCallback(
    (fn: () => void) => () => {
      handleClose();
      fn();
    },
    [handleClose],
  );

  const handleCopyText = useCallback(() => {
    handleClose();
    const selection = window.getSelection()?.toString().trim();
    // If user has an active text selection, copy that; otherwise copy the
    // innerText of the nearest paragraph container.
    const textToCopy =
      selection ||
      (document.querySelector('[data-paragraph-id]') as HTMLElement | null)
        ?.innerText ||
      '';
    void navigator.clipboard.writeText(textToCopy);
  }, [handleClose]);

  return (
    <div onContextMenu={handleContextMenu}>
      {children}
      <Menu
        open={anchorPosition !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition ?? undefined}
      >
        {/* Generate section */}
        <MenuItem onClick={invoke(onImageGenerate)}>
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Generate Image</ListItemText>
        </MenuItem>
        <MenuItem onClick={invoke(onAudioGenerate)}>
          <ListItemIcon>
            <AudiotrackIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Generate Narration</ListItemText>
        </MenuItem>
        <MenuItem onClick={invoke(onTextGenerate)}>
          <ListItemIcon>
            <EditNoteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Generate Text</ListItemText>
        </MenuItem>

        <Divider />

        {/* Edit section */}
        <MenuItem onClick={invoke(onExpand)}>
          <ListItemIcon>
            <AutoFixHighIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Expand Paragraph</ListItemText>
        </MenuItem>
        <MenuItem onClick={invoke(onRewrite)}>
          <ListItemIcon>
            <AutoFixHighIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rewrite</ListItemText>
        </MenuItem>
        <MenuItem onClick={invoke(onSummarize)}>
          <ListItemIcon>
            <ShortTextIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Summarize</ListItemText>
        </MenuItem>

        <Divider />

        {/* Find section */}
        <MenuItem onClick={invoke(onFindSimilar)}>
          <ListItemIcon>
            <CompareArrowsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Find Similar</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyText}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Paragraph Text</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
}
